import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DollarSign, ReceiptText, TrendingUp, Clock, ShoppingBag, AlertTriangle, MailCheck, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { money, fmtTime } from "../lib/format";
import { usePos } from "../context/PosContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center">
          <Icon size={16} />
        </div>
      </div>
      <p className="mt-2 font-mono text-2xl font-bold tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const { outletId, outlet, shift } = usePos();
  const { hasFeature, hasPerm, session } = useAuth();
  const [data, setData] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [emailBannerDismissed, setEmailBannerDismissed] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);

  useEffect(() => {
    const params = outletId ? { outlet_id: outletId } : {};
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [outletId]);

  useEffect(() => {
    let cancelled = false;

    const loadNotifications = async () => {
      setNotificationLoading(true);

      try {
        const response = await api.get("/notifications?limit=20&offset=0");

        if (!cancelled) {
          setNotifications(response.data?.items || []);
        }
      } catch (e) {
        if (!cancelled) {
          setNotifications([]);
        }
      } finally {
        if (!cancelled) {
          setNotificationLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (hasFeature("INVENTORY") && hasPerm("catalog.manage")) {
      api.get("/inventory/low-stock").then((r) => setLowStock(r.data)).catch(() => setLowStock([]));
    }
  }, [hasFeature, hasPerm]);

  const showEmailBanner = session && session.user && session.email_verified === false && !emailBannerDismissed;

  const paymentRejectedNotification =
    notifications.find(
      (notification) =>
        notification.type === "PAYMENT_REJECTED" &&
        !notification.dashboard_dismissed_at
    ) || null;

  const dismissNotification = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/dismiss?surface=dashboard`);

      setNotifications((current) =>
        current.map((notification) =>
          notification.id === notificationId
            ? { ...notification, dashboard_dismissed_at: new Date().toISOString() }
            : notification
        )
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resendVerification = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("A new verification link has been sent to your inbox."));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={t("Dashboard")}
        subtitle={outlet ? `${outlet.name} · ${new Date().toLocaleDateString(i18n.resolvedLanguage === "en" ? "en-US" : "id-ID", { weekday: "long", day: "numeric", month: "long" })}` : ""}
        testid="dashboard-header"
      />
        {paymentRejectedNotification && (
          <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-5 py-4">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-red-900">
                    {paymentRejectedNotification.title}
                  </h3>
                  <p className="mt-1 text-sm text-red-800">
                    {paymentRejectedNotification.message}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => dismissNotification(paymentRejectedNotification.id)}
                  className="shrink-0 rounded-lg p-1 text-red-500 hover:bg-red-100 hover:text-red-700"
                  aria-label={t("Close")}
                  title={t("Close")}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-3">
                <Link
                  to="/settings/subscription"
                  className="inline-flex items-center rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                >
                  {t("View subscription")}
                </Link>
              </div>
            </div>
          </div>
        )}

      {showEmailBanner && (
        <div
          data-testid="verify-email-banner"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-5 py-4"
        >
          <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
            <MailCheck size={18} className="text-amber-400" />
          </div>
          <div className="flex-1 space-y-1">
            <p className="font-heading font-bold text-sm text-amber-100">
              {t("Verify your email")}
            </p>
            <p className="text-xs text-amber-200/80 leading-relaxed">
              <Trans
                i18nKey="Email verification banner copy"
                values={{ email: session.user.email }}
                components={{ email: <span className="font-semibold text-amber-100" /> }}
              />
            </p>
            <button
              onClick={resendVerification}
              disabled={resendBusy}
              data-testid="verify-email-resend"
              className="mt-2 h-8 px-3 rounded-lg bg-amber-500 text-amber-950 text-xs font-bold hover:bg-amber-400 disabled:opacity-60"
            >
              {resendBusy ? t("Sending…") : t("Resend verification link")}
            </button>
          </div>
          <button
            onClick={() => setEmailBannerDismissed(true)}
            data-testid="verify-email-dismiss"
            className="w-8 h-8 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 flex items-center justify-center shrink-0"
            aria-label={t("Close")}
          >
            <X size={16} className="text-amber-300" />
          </button>
        </div>
      )}
      {lowStock.length > 0 && (
        <div data-testid="low-stock-alert" className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2 text-amber-400 font-heading font-semibold">
              <AlertTriangle size={18} /> {t("Low stock ({{count}})", { count: lowStock.length })}
            </div>
            <Link to="/management/ingredients" className="text-xs text-primary font-semibold hover:underline" data-testid="low-stock-manage">{t("Manage stock")}</Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStock.map((i) => (
              <span key={i.id} data-testid={`low-stock-item-${i.id}`} className="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-300 text-xs font-semibold">
                {i.name}: <span className="font-mono">{i.stock_qty}</span> {i.unit}
              </span>
            ))}
          </div>
        </div>
      )}
      {!data ? (
        <p className="text-muted-foreground text-sm">{t("Loading…")}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label={t("Today's Sales")} value={money(data.today.sales)} testid="stat-today-sales" />
            <StatCard icon={ReceiptText} label={t("Today's Orders")} value={data.today.orders} testid="stat-today-orders" />
            <StatCard icon={TrendingUp} label={t("Avg Transaction")} value={money(data.today.avg_transaction)} testid="stat-avg-transaction" />
            <StatCard
              icon={Clock}
              label={t("Current Shift")}
              value={shift ? t("Shift open status") : "—"}
              sub={shift ? t("{{cashier}} · opened {{time}}", { cashier: shift.cashier_name, time: fmtTime(shift.opened_at) }) : t("No active shift at this outlet")}
              testid="stat-current-shift"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold">{t("Recent Orders")}</h3>
                <Link to="/orders" className="text-xs text-primary font-semibold hover:underline" data-testid="view-all-orders">{t("View all")}</Link>
              </div>
              {data.recent_orders.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">{t("No orders yet today. Open POS to start selling.")}</p>
              ) : (
                <div className="divide-y divide-border/60">
                  {data.recent_orders.map((o) => (
                    <div key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-xs text-primary truncate">{o.transaction_number}</p>
                        <p className="text-xs text-muted-foreground">{o.cashier?.name} · {fmtTime(o.created_at)}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-mono text-sm font-bold">{money(o.grand_total)}</p>
                        <p className="text-[11px] text-muted-foreground">{o.payment?.method_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-3">{t("Payments Today")}</h3>
                {data.payment_summary.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("No payments yet.")}</p>
                ) : (
                  <div className="space-y-2">
                    {data.payment_summary.map((p) => (
                      <div key={p.method} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{p.method} <span className="text-xs">({p.count})</span></span>
                        <span className="font-mono font-semibold">{money(p.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-heading font-semibold mb-3">{t("Top Products")}</h3>
                {data.top_products.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("No sales yet.")}</p>
                ) : (
                  <div className="space-y-2">
                    {data.top_products.map((p, i) => (
                      <div key={p.name} className="flex items-center gap-2.5 text-sm">
                        <span className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center text-[11px] font-bold text-primary">{i + 1}</span>
                        <span className="flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{t("{{count}} sold", { count: p.qty })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/pos"
                data-testid="go-to-pos"
                className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 active:scale-[0.98] transition"
              >
                <ShoppingBag size={18} /> {t("Open POS")}
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
